import { LiveData, Service } from '@toeverything/infra';

import { getActivePageIds } from '../../../hooks/use-active-pages';
import type { WorkspacePropertiesAdapter } from './adapter';
import { attendeeExistsByEmail, normalizeEmail } from './attendee-id-utils';
import type { SerializedAttendee, SerializedTodoItem } from './schema';
import { deduplicateAttendees, filterActionItemsByCompletion, isActionItemCompleted } from './utils';

interface Disposable {
  dispose: () => void;
}

export interface PageAttendeesGroup {
  pageId: string;
  pageTitle: string;
  createDate: number;
  updateDate: number;
  attendees: SerializedAttendee[];
}

export interface AttendeeActionItemGroup {
  attendee: SerializedAttendee;
  actionItems: SerializedTodoItem[];
  totalItems: number;
  completedItems: number;
}

// Filter for querying documents by attendees
export type AttendeeFilter = {
  readonly attendeeEmailsIn?: readonly string[]; // Documents containing ANY of these attendees
  readonly attendeeEmailsAll?: readonly string[]; // Documents containing ALL of these attendees
  readonly attendeeEmailsNotIn?: readonly string[]; // Documents NOT containing these attendees
  readonly pageIdIn?: readonly string[]; // Limit to specific pages
  readonly pageIdNotIn?: readonly string[]; // Exclude specific pages
  readonly includeEmpty?: boolean; // Include pages with no attendees (default: false)
};

export interface DocumentWithAttendees {
  pageId: string;
  pageTitle: string;
  createDate: number;
  updateDate: number;
  attendees: SerializedAttendee[];
  matchingAttendees: SerializedAttendee[]; // Subset of attendees that matched the filter
}

export class AttendeesService extends Service {
  private readonly _attendees$ = new LiveData<Record<string, SerializedAttendee[]>>({});
  private readonly _attendeesGrouped$ = new LiveData<PageAttendeesGroup[]>([]);
  private _propertiesSubscription: Disposable | null = null;
  private _docMetaSubscription: Disposable | null = null;
  private _isDisposed = false;

  constructor(private readonly propertiesAdapter: WorkspacePropertiesAdapter) {
    super();
    this._refreshObservables();
    const subscription = this.propertiesAdapter.properties$.subscribe(() => {
      if (this._isDisposed) return;
      this._refreshObservables();
    });
    this._propertiesSubscription = {
      dispose: () => subscription.unsubscribe(),
    };

    const docMeta = this.propertiesAdapter.workspace?.docCollection?.meta;
    if (docMeta) {
      this._docMetaSubscription = docMeta.docMetaUpdated.on(() => {
        if (this._isDisposed) return;
        this._refreshObservables();
      });
    }
  }


  private _getValidPageIds(): string[] {
    const workspace = this.propertiesAdapter.workspace;
    return getActivePageIds(workspace.docCollection);
  }

  /**
   * @private
   */
  private _getPageMeta(pageId: string) {
    const workspace = this.propertiesAdapter.workspace;
    const pageMeta = workspace.docCollection.meta.getDocMeta(pageId);
    return pageMeta ? {
      title: pageMeta.title || 'Untitled',
      createDate: pageMeta.createDate || Date.now(),
      updateDate: pageMeta.updatedDate || Date.now(),
    } : null;
  }

  /**
   * @private
   */
  private _refreshObservables() {
    this._attendees$.value = this.getAllAttendees();
    this._attendeesGrouped$.value = this.getAllAttendeesGrouped();
  }


  getPageAttendees(pageId: string): SerializedAttendee[] {
    const rawAttendees = this.propertiesAdapter.getPageAttendees(pageId);
    return deduplicateAttendees(rawAttendees);
  }

  pageAttendees$(pageId: string): LiveData<SerializedAttendee[]> {
    return this.propertiesAdapter.properties$.map(() =>
      this.getPageAttendees(pageId)
    );
  }


  getAllAttendees(): Record<string, SerializedAttendee[]> {
    const result: Record<string, SerializedAttendee[]> = {};

    this._getValidPageIds().forEach(pageId => {
      const attendees = this.getPageAttendees(pageId);
      if (attendees.length > 0) {
        result[pageId] = attendees;
      }
    });

    return result;
  }


  getAllUniqueAttendees(): SerializedAttendee[] {
    const allPageAttendees = Object.values(this.getAllAttendees()).flat();
    return deduplicateAttendees(allPageAttendees);
  }


  getAllAttendeesGrouped(): PageAttendeesGroup[] {
    const allAttendees = this.getAllAttendees();
    const groups: PageAttendeesGroup[] = [];

    Object.entries(allAttendees).forEach(([pageId, attendees]) => {
      const pageMeta = this._getPageMeta(pageId);
      if (pageMeta && attendees.length > 0) {
        groups.push({
          pageId,
          pageTitle: pageMeta.title,
          createDate: pageMeta.createDate,
          updateDate: pageMeta.updateDate,
          attendees,
        });
      }
    });

    return groups.sort((a, b) => b.updateDate - a.createDate);
  }

  findAttendeesByEmail(email: string): SerializedAttendee[] {
    const uniqueAttendees = this.getAllUniqueAttendees();
    const normalizedEmail = normalizeEmail(email);
    return uniqueAttendees.filter(attendee =>
      normalizeEmail(attendee.email) === normalizedEmail
    );
  }

  /**
   * Query documents by attendee filters - similar to ActionItemsService.queryItemsAcrossDocs
   */
  queryDocumentsByAttendees(filter?: AttendeeFilter): DocumentWithAttendees[] {
    const attendeeEmailsIn = filter?.attendeeEmailsIn || [];
    const attendeeEmailsAll = filter?.attendeeEmailsAll || [];
    const attendeeEmailsNotIn = filter?.attendeeEmailsNotIn || [];
    const pageIdIn = filter?.pageIdIn;
    const pageIdNotIn = filter?.pageIdNotIn || [];
    const includeEmpty = filter?.includeEmpty || false;

    const documents: DocumentWithAttendees[] = [];
    const allAttendees = this.getAllAttendees();

    Object.entries(allAttendees).forEach(([pageId, attendees]) => {
      // Apply page filters
      if (pageIdIn && !pageIdIn.includes(pageId)) return;
      if (pageIdNotIn.includes(pageId)) return;

      const pageMeta = this._getPageMeta(pageId);
      if (!pageMeta) return;

      // Normalize attendee emails for comparison
      const normalizedAttendees = attendees.map(a => ({
        ...a,
        normalizedEmail: normalizeEmail(a.email)
      }));

      const normalizedEmailsIn = attendeeEmailsIn.map(email => normalizeEmail(email));
      const normalizedEmailsAll = attendeeEmailsAll.map(email => normalizeEmail(email));
      const normalizedEmailsNotIn = attendeeEmailsNotIn.map(email => normalizeEmail(email));

      // Apply attendee filters
      let matchingAttendees: SerializedAttendee[] = [];
      let shouldInclude = true;

      // Check if document should be excluded (attendeeEmailsNotIn)
      if (normalizedEmailsNotIn.length > 0) {
        const hasExcludedAttendee = normalizedAttendees.some(a =>
          normalizedEmailsNotIn.includes(a.normalizedEmail)
        );
        if (hasExcludedAttendee) {
          shouldInclude = false;
        }
      }

      if (shouldInclude) {
        // Check attendeeEmailsAll (ALL must be present)
        if (normalizedEmailsAll.length > 0) {
          const hasAllRequired = normalizedEmailsAll.every(requiredEmail =>
            normalizedAttendees.some(a => a.normalizedEmail === requiredEmail)
          );
          if (!hasAllRequired) {
            shouldInclude = false;
          } else {
            // Include attendees that match the "all" filter
            matchingAttendees = attendees.filter(a =>
              normalizedEmailsAll.includes(normalizeEmail(a.email))
            );
          }
        }

        // Check attendeeEmailsIn (ANY can be present)
        if (shouldInclude && normalizedEmailsIn.length > 0) {
          const matchingInAttendees = attendees.filter(a =>
            normalizedEmailsIn.includes(normalizeEmail(a.email))
          );
          if (matchingInAttendees.length === 0) {
            shouldInclude = false;
          } else {
            // If we didn't already set matching attendees from "all" filter
            if (matchingAttendees.length === 0) {
              matchingAttendees = matchingInAttendees;
            } else {
              // Combine both "all" and "in" matches, deduplicated
              const combined = [...matchingAttendees, ...matchingInAttendees];
              matchingAttendees = this.deduplicateAttendees(combined);
            }
          }
        }

        // If no specific filters, include all attendees as matching
        if (shouldInclude && normalizedEmailsIn.length === 0 && normalizedEmailsAll.length === 0) {
          matchingAttendees = attendees;
        }
      }

      // Handle empty attendee pages
      if (attendees.length === 0) {
        if (includeEmpty && shouldInclude) {
          documents.push({
            pageId,
            pageTitle: pageMeta.title,
            createDate: pageMeta.createDate,
            updateDate: pageMeta.updateDate,
            attendees: [],
            matchingAttendees: []
          });
        }
        return;
      }

      if (shouldInclude && matchingAttendees.length > 0) {
        documents.push({
          pageId,
          pageTitle: pageMeta.title,
          createDate: pageMeta.createDate,
          updateDate: pageMeta.updateDate,
          attendees,
          matchingAttendees
        });
      }
    });

    // Sort by update date (most recent first)
    return documents.sort((a, b) => b.updateDate - a.updateDate);
  }

  /**
   * AND-capable query across multiple attendee filters.
   * Each filter uses schema-backed properties (emails only) and results are AND'ed.
   * If no filters are provided, all documents with attendees are returned (like queryDocumentsByAttendees with no args).
   */
  queryDocumentsByAttendeeFilters(
    filters?: readonly AttendeeFilter[],
    options?: { pageIdIn?: readonly string[]; pageIdNotIn?: readonly string[]; includeEmpty?: boolean }
  ): DocumentWithAttendees[] {
    const filtersArray: readonly AttendeeFilter[] = filters && filters.length > 0 ? filters : [{}];

    const pageIdIn = options?.pageIdIn;
    const pageIdNotIn = options?.pageIdNotIn || [];
    const includeEmpty = options?.includeEmpty || false;

    const documents: DocumentWithAttendees[] = [];
    const allAttendees = this.getAllAttendees();

    Object.entries(allAttendees).forEach(([pageId, attendees]) => {
      // Apply page filters first
      if (pageIdIn && !pageIdIn.includes(pageId)) return;
      if (pageIdNotIn.includes(pageId)) return;

      const pageMeta = this._getPageMeta(pageId);
      if (!pageMeta) return;

      // Optionally include empty attendee pages
      if (attendees.length === 0) {
        if (includeEmpty) {
          documents.push({
            pageId,
            pageTitle: pageMeta.title,
            createDate: pageMeta.createDate,
            updateDate: pageMeta.updateDate,
            attendees: [],
            matchingAttendees: []
          });
        }
        return;
      }

      // Normalize attendees for email-based matching (single source of truth)
      const normalizedAttendees = attendees.map(a => ({
        ...a,
        normalizedEmail: normalizeEmail(a.email)
      }));

      // Helper to evaluate a single filter against this page
      const matchesSingleFilter = (filter: AttendeeFilter): { ok: boolean; matchedEmails: Set<string> } => {
        const inSet = new Set((filter.attendeeEmailsIn || []).map(normalizeEmail));
        const allSet = new Set((filter.attendeeEmailsAll || []).map(normalizeEmail));
        const notInSet = new Set((filter.attendeeEmailsNotIn || []).map(normalizeEmail));

        // Exclusions
        if (notInSet.size > 0) {
          const hasExcluded = normalizedAttendees.some(a => notInSet.has(a.normalizedEmail));
          if (hasExcluded) return { ok: false, matchedEmails: new Set<string>() };
        }

        // ALL must be present
        if (allSet.size > 0) {
          const hasAll = Array.from(allSet).every(req => normalizedAttendees.some(a => a.normalizedEmail === req));
          if (!hasAll) return { ok: false, matchedEmails: new Set<string>() };
        }

        // IN: if provided, require at least one
        if (inSet.size > 0) {
          const anyIn = normalizedAttendees.some(a => inSet.has(a.normalizedEmail));
          if (!anyIn) return { ok: false, matchedEmails: new Set<string>() };
        }

        // Collect matched emails for this filter (union of ALL and IN constraints)
        const matchedEmails = new Set<string>();
        if (allSet.size > 0) allSet.forEach(e => matchedEmails.add(e));
        if (inSet.size > 0) inSet.forEach(e => matchedEmails.add(e));
        // If neither ALL nor IN provided, treat as pass-through filter (no specific matches)
        return { ok: true, matchedEmails };
      };

      // Evaluate all filters with AND semantics
      const aggregateMatchedEmails = new Set<string>();
      for (const f of filtersArray) {
        const { ok, matchedEmails } = matchesSingleFilter(f);
        if (!ok) return; // Any filter failing excludes the document
        matchedEmails.forEach(e => aggregateMatchedEmails.add(e));
      }

      // Compute matchingAttendees based on aggregate matched emails (if any specified)
      let matchingAttendees = attendees;
      if (aggregateMatchedEmails.size > 0) {
        matchingAttendees = attendees.filter(a => aggregateMatchedEmails.has(normalizeEmail(a.email)));
      }

      documents.push({
        pageId,
        pageTitle: pageMeta.title,
        createDate: pageMeta.createDate,
        updateDate: pageMeta.updateDate,
        attendees,
        matchingAttendees
      });
    });

    return documents.sort((a, b) => b.updateDate - a.updateDate);
  }

  /**
   * Get documents containing ALL of the specified attendees
   */
  getDocumentsWithAllAttendees(attendeeEmails: string[], excludePageIds?: string[]): DocumentWithAttendees[] {
    return this.queryDocumentsByAttendees({
      attendeeEmailsAll: attendeeEmails,
      pageIdNotIn: excludePageIds
    });
  }

  /**
   * Get documents containing ANY of the specified attendees
   */
  getDocumentsWithAnyAttendees(attendeeEmails: string[], excludePageIds?: string[]): DocumentWithAttendees[] {
    return this.queryDocumentsByAttendees({
      attendeeEmailsIn: attendeeEmails,
      pageIdNotIn: excludePageIds
    });
  }

  groupActionItemsByAttendees(
    actionItems: Record<string, SerializedTodoItem[]>,
    includeCompleted: boolean = false
  ): AttendeeActionItemGroup[] {
    const allUniqueAttendees = this.getAllUniqueAttendees();

    if (allUniqueAttendees.length === 0) {
      return [];
    }

    const allActionItems: SerializedTodoItem[] = [];
    Object.entries(actionItems).forEach(([pageId, items]) => {
      items.forEach(item => {
        allActionItems.push({ ...item, pageId });
      });
    });

    const filteredItems = filterActionItemsByCompletion(allActionItems, includeCompleted);

    const groups: AttendeeActionItemGroup[] = [];

    allUniqueAttendees.forEach(attendee => {
      if (!attendee.email) return;

      const attendeeEmail = normalizeEmail(attendee.email);

      const assignedItems = filteredItems.filter(item => {
        if (!item.assignedTo || item.assignedTo.length === 0) return false;

        return item.assignedTo.some(assignee =>
          assignee.email && normalizeEmail(assignee.email) === attendeeEmail
        );
      });

      const allAssignedItems = allActionItems.filter(item => {
        if (!item.assignedTo || item.assignedTo.length === 0) return false;

        return item.assignedTo.some(assignee =>
          assignee.email && normalizeEmail(assignee.email) === attendeeEmail
        );
      });

      if (allAssignedItems.length > 0) {
        const completedItems = allAssignedItems.filter(item => isActionItemCompleted(item));

        groups.push({
          attendee,
          actionItems: assignedItems,
          totalItems: allAssignedItems.length,
          completedItems: completedItems.length
        });
      }
    });

    return groups.sort((a, b) => {
      const nameA = a.attendee.name || a.attendee.email || '';
      const nameB = b.attendee.name || b.attendee.email || '';
      return nameA.localeCompare(nameB);
    });
  }

  getActionItemsForAttendee(
    attendeeEmail: string,
    actionItems: Record<string, SerializedTodoItem[]>,
    includeCompleted: boolean = false
  ): SerializedTodoItem[] {
    const groups = this.groupActionItemsByAttendees(actionItems, includeCompleted);
    const normalizedEmail = normalizeEmail(attendeeEmail);
    const attendeeGroup = groups.find(group =>
      normalizeEmail(group.attendee.email) === normalizedEmail
    );

    return attendeeGroup ? attendeeGroup.actionItems : [];
  }


  deduplicateAttendees(attendees: SerializedAttendee[]): SerializedAttendee[] {
    return deduplicateAttendees(attendees);
  }


  get attendees$() {
    return this._attendees$;
  }

  get attendeesGrouped$() {
    return this._attendeesGrouped$;
  }

  addPageAttendee(pageId: string, attendee: SerializedAttendee): void {
    if (!attendee.email || !attendee.email.trim()) {
      throw new Error('Email is required to add attendee');
    }

    const existingAttendees = this.getPageAttendees(pageId);
    const exists = attendeeExistsByEmail(existingAttendees, attendee.email);

    if (!exists) {
      this.propertiesAdapter.addPageAttendee(pageId, attendee);
      this._refreshObservables();
    }
  }


  updatePageAttendee(pageId: string, attendeeId: string, updates: Partial<SerializedAttendee>): void {
    this.propertiesAdapter.updatePageAttendee(pageId, attendeeId, updates);
    this._refreshObservables();
  }

  removePageAttendee(pageId: string, attendeeId: string): void {
    this.propertiesAdapter.removePageAttendee(pageId, attendeeId);
    this._refreshObservables();
  }


  updatePageAttendees(pageId: string, attendees: SerializedAttendee[]): void {
    this.propertiesAdapter.setPageAttendees(pageId, attendees);
    this._refreshObservables();
  }


  removePageAttendees(pageId: string): void {
    this.propertiesAdapter.setPageAttendees(pageId, []);
    this._refreshObservables();
  }

  public dispose(): void {
    this._isDisposed = true;
    if (this._propertiesSubscription) {
      this._propertiesSubscription.dispose();
      this._propertiesSubscription = null;
    }
    if (this._docMetaSubscription) {
      this._docMetaSubscription.dispose();
      this._docMetaSubscription = null;
    }
    super.dispose?.();
  }

  /**
   * Get all attendee emails from the current event attendees
   */
  normalizeAttendeeEmails(attendees: SerializedAttendee[]): string[] {
    return attendees
      .map(a => a.email)
      .filter(Boolean)
      .map(email => normalizeEmail(email));
  }
}
